import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Message, ContentBlock } from '@/types';

export interface ChatState {
  activeConversationId: string | null;
  messagesByConversation: Record<string, Message[]>;
  streaming: boolean;
  streamingMessageId: string | null;
  streamingProgress: string | null;
}

const initialState: ChatState = {
  activeConversationId: null,
  messagesByConversation: {},
  streaming: false,
  streamingMessageId: null,
  streamingProgress: null,
};

interface UpdateMessageContentPayload {
  conversationId: string;
  messageId: string;
  content: ContentBlock[];
}

interface SetMessageStatusPayload {
  conversationId: string;
  messageId: string;
  status: Message['status'];
}

interface SetStreamingPayload {
  streaming: boolean;
  messageId: string | null;
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setActiveConversation(state, action: PayloadAction<string | null>) {
      state.activeConversationId = action.payload;
    },

    addMessage(state, action: PayloadAction<Message>) {
      const msg = action.payload;
      const convId = msg.conversationId;

      const messages = (state.messagesByConversation[convId] ??= []);

      // Prevent duplicate messages
      const existing = messages.find((m) => m.id === msg.id);
      if (!existing) {
        messages.push(msg);
      }
    },

    updateMessageContent(state, action: PayloadAction<UpdateMessageContentPayload>) {
      const { conversationId, messageId, content } = action.payload;
      const messages = state.messagesByConversation[conversationId];
      if (messages) {
        const msg = messages.find((m) => m.id === messageId);
        if (msg) {
          msg.content = content;
        }
      }
    },

    setMessageStatus(state, action: PayloadAction<SetMessageStatusPayload>) {
      const { conversationId, messageId, status } = action.payload;
      const messages = state.messagesByConversation[conversationId];
      if (messages) {
        const msg = messages.find((m) => m.id === messageId);
        if (msg) {
          msg.status = status;
        }
      }
    },

    setStreaming(state, action: PayloadAction<SetStreamingPayload>) {
      state.streaming = action.payload.streaming;
      state.streamingMessageId = action.payload.messageId;
      if (!action.payload.streaming) {
        state.streamingProgress = null;
      }
    },

    setStreamingProgress(state, action: PayloadAction<string | null>) {
      state.streamingProgress = action.payload;
    },

    clearChat(state, action: PayloadAction<string>) {
      const convId = action.payload;
      state.messagesByConversation[convId] = [];
      state.streaming = false;
      state.streamingMessageId = null;
      state.streamingProgress = null;
    },
  },
});

export const {
  setActiveConversation,
  addMessage,
  updateMessageContent,
  setMessageStatus,
  setStreaming,
  setStreamingProgress,
  clearChat,
} = chatSlice.actions;

// Selectors
export const selectActiveConversationId = (state: { chat: ChatState }): string | null =>
  state.chat.activeConversationId;

const EMPTY_MESSAGES: Message[] = [];
export const selectMessages = (state: { chat: ChatState }, conversationId: string): Message[] =>
  state.chat.messagesByConversation[conversationId] ?? EMPTY_MESSAGES;

export const selectIsStreaming = (state: { chat: ChatState }): boolean => state.chat.streaming;

export const selectStreamingProgress = (state: { chat: ChatState }): string | null =>
  state.chat.streamingProgress;

export default chatSlice.reducer;
